/**
 * useChartSyncStore — cross-chart dashboard synchronisation state.
 *
 * Part of the useDisplayStore split (architecture review item 5). Holds the
 * `syncedCharts` registry that lets DashboardPanel-rendered charts coordinate
 * brush selections, hovered points, and shared dimensions.
 *
 * Layout/sidebar state lives in `useLayoutStore`; theme/modals/locale live
 * in `useChromeStore`.
 */

import { defineStore } from 'pinia';

export const useChartSyncStore = defineStore('chartSync', {
  state: () => ({
    syncedCharts: {},
  }),

  getters: {
    getSyncValues: (state) => (payload) => {
      const syncId = payload.syncId;
      const syncWhat = payload.syncWhat;
      const syncValues = [];

      if (!Object.prototype.hasOwnProperty.call(state.syncedCharts, syncId)) {
        return false;
      }

      Object.keys(state.syncedCharts[syncId]).forEach((key) => {
        if (state.syncedCharts[syncId][key] !== null) {
          const syncDimensions = state.syncedCharts[syncId][key].syncDimensions;
          if (syncDimensions !== undefined) {
            Object.keys(syncDimensions).forEach((dimension) => {
              if (
                dimension === syncWhat &&
                syncDimensions[dimension] !== null &&
                syncDimensions[dimension] !== undefined
              ) {
                syncValues.push(syncDimensions[dimension]);
              }
            });
          }
        }
      });

      return syncValues;
    },
  },

  actions: {
    resetSyncedCharts(payload) {
      const syncId = payload?.syncId;
      if (!syncId) return;
      this.syncedCharts = { ...this.syncedCharts, [syncId]: {} };
    },

    syncCharts(payload) {
      const { syncId, syncWhat, syncDimensions, uuid } = payload;
      const next = { ...this.syncedCharts };
      if (!Object.prototype.hasOwnProperty.call(next, syncId)) {
        next[syncId] = { syncWhat };
      }
      next[syncId] = { ...next[syncId], [uuid.value]: { syncDimensions } };
      this.syncedCharts = next;
    },

    removeSyncedChart(payload) {
      const { syncId, uuid } = payload;
      if (!Object.prototype.hasOwnProperty.call(this.syncedCharts, syncId)) return;
      if (Object.prototype.hasOwnProperty.call(this.syncedCharts[syncId], uuid)) {
        const { [uuid]: _removed, ...rest } = this.syncedCharts[syncId];
        this.syncedCharts = { ...this.syncedCharts, [syncId]: rest };
      }
    },

    syncChart(payload = null) {
      this.syncCharts(payload);
    },
  },
});
